import { NotificationFragment } from "@graphql/notifications/Notifications.g";
import { NotificationEnum } from "@graphql/generated/graphql.g";
import React from "react";

import { NotificationTypeMappingText } from "@components/Texts";

import { Box } from "@design-system/Box/Box";
import { Clickable } from "@design-system/Clickable/Clickable";
import { Dropdown } from "@design-system/Dropdown/Dropdown";
import { IconButton } from "@design-system/IconButton/IconButton";
import { Table } from "@design-system/Table/Table";
import { Text } from "@design-system/Text/Text";

import { createTableConfiguration } from "@utils/table/TableConfigurationSystem";
import {
  getNotificationReasonLabel,
  NO_EMPLOYEE_IDENTIFIED,
} from "@utils/notificationReasons";
import { ClientNotificationFilterFormValues } from "@components/Forms/ClientNotificationFilterForm/ClientNotificationFilterForm";

import {
  CLIENT_NOTIFICATION_TABLE_COLUMN_LABELS,
  ClientNotificationTableColumns,
  DEFAULT_CLIENT_NOTIFICATION_TABLE_COLUMN_ORDER,
  DEFAULT_CLIENT_NOTIFICATION_TABLE_COLUMN_SETTINGS,
  DEFAULT_HISTORY_CLIENT_NOTIFICATION_TABLE_COLUMN_SETTINGS,
} from "./UI/ClientNotificationsTableColumns";

/**
 * Default filter values for Client Notifications
 */
export const DEFAULT_CLIENT_NOTIFICATION_FILTER_VALUES: ClientNotificationFilterFormValues =
  {
    notificationType: [],
    applicationNameSearch: "",
    resolvedById: undefined,
    resolvedAt_Gte: undefined,
    resolvedAt_Lte: undefined,
  };

/**
 * Comprehensive table configuration for Client Notifications
 */
export const ClientNotificationsTableConfig = createTableConfiguration<
  NotificationFragment,
  ClientNotificationTableColumns
>({
  tableId: "client-notifications-v3",
  columns: {
    [ClientNotificationTableColumns.applicationName]: {
      id: ClientNotificationTableColumns.applicationName,
      label:
        CLIENT_NOTIFICATION_TABLE_COLUMN_LABELS[
          ClientNotificationTableColumns.applicationName
        ],
      fieldPath: "application.resource.productName",
      sortField: "resource__product_name",
      type: "custom",
      defaultVisible: false,
      render: (value: unknown, row: NotificationFragment) => {
        return renderApplicationName(value, row, () => {});
      },
    },
    [ClientNotificationTableColumns.employee]: {
      id: ClientNotificationTableColumns.employee,
      label:
        CLIENT_NOTIFICATION_TABLE_COLUMN_LABELS[
          ClientNotificationTableColumns.employee
        ],
      fieldPath: "employee.name",
      sortField: "employee__name",
      type: "custom",
      defaultVisible: true,
      render: (_value: unknown, row: NotificationFragment) =>
        renderEmployee(_value, row),
    },
    [ClientNotificationTableColumns.reason]: {
      id: ClientNotificationTableColumns.reason,
      label:
        CLIENT_NOTIFICATION_TABLE_COLUMN_LABELS[
          ClientNotificationTableColumns.reason
        ],
      fieldPath: "reason",
      sortField: "reason",
      type: "custom",
      defaultVisible: true,
      render: (_value: unknown, row: NotificationFragment) =>
        renderReason(_value, row),
    },
    [ClientNotificationTableColumns.notificationType]: {
      id: ClientNotificationTableColumns.notificationType,
      label:
        CLIENT_NOTIFICATION_TABLE_COLUMN_LABELS[
          ClientNotificationTableColumns.notificationType
        ],
      fieldPath: "notificationType",
      sortField: "notification_type",
      type: "custom",
      defaultVisible: false,
      render: (value: unknown, row: NotificationFragment) => {
        return renderNotificationType(value, row, () => {});
      },
    },
    [ClientNotificationTableColumns.createdAt]: {
      id: ClientNotificationTableColumns.createdAt,
      label:
        CLIENT_NOTIFICATION_TABLE_COLUMN_LABELS[
          ClientNotificationTableColumns.createdAt
        ],
      fieldPath: "createdAt",
      sortField: "created_at",
      type: "date",
      defaultVisible: true,
      render: (value: unknown) => {
        return renderCreatedAt(value);
      },
    },
    [ClientNotificationTableColumns.resolvedBy]: {
      id: ClientNotificationTableColumns.resolvedBy,
      label:
        CLIENT_NOTIFICATION_TABLE_COLUMN_LABELS[
          ClientNotificationTableColumns.resolvedBy
        ],
      fieldPath: "resolvedBy.name",
      sortField: "resolved_by_id",
      type: "custom",
      defaultVisible: true,
      render: (value: unknown, row: NotificationFragment) =>
        renderResolvedBy(value, row),
    },
    [ClientNotificationTableColumns.resolvedAt]: {
      id: ClientNotificationTableColumns.resolvedAt,
      label:
        CLIENT_NOTIFICATION_TABLE_COLUMN_LABELS[
          ClientNotificationTableColumns.resolvedAt
        ],
      fieldPath: "resolvedAt",
      sortField: "resolved_at",
      type: "date",
      defaultVisible: true,
      render: (value: unknown) => renderResolvedAt(value),
    },
  },
  defaultColumnOrder: DEFAULT_CLIENT_NOTIFICATION_TABLE_COLUMN_ORDER,
  defaultColumnSettings: DEFAULT_CLIENT_NOTIFICATION_TABLE_COLUMN_SETTINGS,
});

export const ClientNotificationsHistoryTableConfig = {
  ...ClientNotificationsTableConfig,
  tableId: "client-notifications-history-v2",
  defaultColumnSettings:
    DEFAULT_HISTORY_CLIENT_NOTIFICATION_TABLE_COLUMN_SETTINGS,
};

/**
 * Helper function to get default filter values
 */
export const getDefaultFilterValues = (): ClientNotificationFilterFormValues => ({
  ...DEFAULT_CLIENT_NOTIFICATION_FILTER_VALUES,
});

/**
 * Helper function to check if filters are active
 */
export const areFiltersActive = (
  filterValues: ClientNotificationFilterFormValues,
  includeHistoryFilters = false,
): boolean => {
  const baseFiltersActive = Boolean(
    (filterValues.notificationType &&
      filterValues.notificationType.length > 0) ||
      (filterValues.applicationNameSearch &&
        filterValues.applicationNameSearch.trim()),
  );

  if (!includeHistoryFilters) {
    return baseFiltersActive;
  }

  return Boolean(
    baseFiltersActive ||
      filterValues.resolvedById ||
      filterValues.resolvedAt_Gte ||
      filterValues.resolvedAt_Lte,
  );
};

/**
 * Custom render functions for specific columns
 */
export function renderApplicationName(
  _value: unknown,
  row: NotificationFragment,
  onApplicationDashboardClick: (clientId: string, applicationId: string) => void,
): React.ReactNode {
  const mainApp = row.application?.resourceGroupApplications?.primaryApplication || row.application;
  return (
    <Clickable
      onClick={() => onApplicationDashboardClick(
        row.client?.id || "",
        row.application?.id || "",
      )}
      outline="sm"
      stretch
    >
      <Table.CompanyCell
        name={
          mainApp?.resource.productName ||
          "Unknown Notification"
        }
        signedLogoUrl={
          mainApp?.resource.publicResource
            ?.signedLogoUrl || undefined
        }
      />
    </Clickable>
  );
}

export function renderEmployee(
  _value: unknown,
  row: NotificationFragment,
): React.ReactNode {
  if (!row.employee) {
    return (
      <Text size={14} color="textSecondary">
        {NO_EMPLOYEE_IDENTIFIED}
      </Text>
    );
  }

  return (
    <Table.UserCell
      name={row.employee.name}
      email={row.employee.email || undefined}
    />
  );
}

export function renderReason(
  _value: unknown,
  row: NotificationFragment,
): React.ReactNode {
  return (
    <Text size={14}>
      {getNotificationReasonLabel(row.reason, row.notificationType)}
    </Text>
  );
}

export function renderNotificationType(
  value: unknown,
  row: NotificationFragment,
  onNotificationClick: (notificationId: string) => void,
): React.ReactNode {
  if (value === null || value === undefined) {
    return null;
  }
  return (
    <Clickable
      onClick={() => onNotificationClick(row.id)}
      outline="sm"
      stretch
    >
      <NotificationTypeMappingText notificationType={value as NotificationEnum} />
    </Clickable>
  );
}

export function renderCreatedAt(value: unknown): React.ReactNode {
  if (!value) {
    return <Text size={14}>—</Text>;
  }

  return <Table.DateCell date={new Date(value as string)} />;
}

export function renderResolvedBy(
  value: unknown,
  row: NotificationFragment,
): React.ReactNode {
  const name =
    (value as string | undefined) ||
    row.resolvedBy?.name ||
    row.resolvedBy?.email ||
    "—";

  return <Text size={14}>{name}</Text>;
}

export function renderResolvedAt(value: unknown): React.ReactNode {
  if (!value) {
    return <Text size={14}>—</Text>;
  }

  return <Table.DateCell date={new Date(value as string)} />;
}

export function renderActions(
  _value: unknown,
  row: NotificationFragment,
  onDetailsClick: (notificationId: string) => void,
  onResolveClick: (id: string) => void,
): React.ReactNode {
  return (
    <Box gap={2}>
      <Dropdown
        trigger={<IconButton size="sm" icon="faEllipsisVertical" />}
      >
        <Dropdown.Option
          label="Details"
          icon="faFileLines"
          onSelect={() =>
            onDetailsClick(row.id)
          }
        />
        <Dropdown.Option
          label="Resolve"
          icon="faCheck"
          onSelect={() => onResolveClick(row.id)}
        />
      </Dropdown>
    </Box>
  );
}
